import { useCallback, useEffect, useMemo, useState } from 'react';
import { SerializedAccount, SerializedFile } from 'fym-common/src/db/serializersTypes';
import { useUpdateAccountMutation } from '../../../../redux/api/fymAPI';

interface ArtistValues {
  name: string;
  avatar?: SerializedFile;
}

function initialValues(account: SerializedAccount | undefined): ArtistValues {
  return { name: account?.name ?? '', avatar: account?.avatarFile ?? undefined };
}

/**
 * The name and the avatar of the active artist as one draft (design artist-settings a1).
 *
 * Save sends one account PATCH with the name and the avatar. The server purges the cached public
 * pages after it (onAccountAppearanceChanged).
 */
export function useArtistForm(account: SerializedAccount | undefined) {
  const initial = useMemo(() => initialValues(account), [account]);
  const [values, setValues] = useState<ArtistValues>(initial);
  const [updateAccount, { isLoading, error }] = useUpdateAccountMutation();

  // The account arrives after the first render, and again after every save.
  useEffect(() => setValues(initial), [initial]);

  const setName = useCallback((name: string) => setValues((current) => ({ ...current, name })), []);
  const setAvatar = useCallback(
    (avatar: SerializedFile | undefined) => setValues((current) => ({ ...current, avatar })),
    []
  );
  const discard = useCallback(() => setValues(initial), [initial]);

  const dirtyCount = useMemo(
    () => (values.name !== initial.name ? 1 : 0) + (values.avatar?.id !== initial.avatar?.id ? 1 : 0),
    [initial.avatar?.id, initial.name, values.avatar?.id, values.name]
  );
  const canSave = dirtyCount > 0 && values.name.trim() !== '';

  const save = useCallback(async (): Promise<void> => {
    if (account === undefined) {
      return;
    }
    // The address is left out, so the PATCH does not touch it. No avatar sends null, which removes it.
    await updateAccount({ id: account.id, name: values.name, avatarFileId: values.avatar?.id });
  }, [account, updateAccount, values.avatar?.id, values.name]);

  return { values, setName, setAvatar, discard, dirtyCount, canSave, save, isSaving: isLoading, error };
}
